import { useState } from 'react';
import type { CashFlowPeriod } from '../../../hooks/useReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface Props {
  cf30: CashFlowPeriod;
  cf60: CashFlowPeriod;
  cf90: CashFlowPeriod;
  loading: boolean;
}

type Period = 30 | 60 | 90;

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—';

const RECURRENCE_PT: Record<string, string> = {
  monthly: 'Mensal', weekly: 'Semanal', annual: 'Anual', daily: 'Diário', biweekly: 'Quinzenal',
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-[12px]">
      <p className="font-600 text-gray-700 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.fill }} className="font-500">{p.name}: {fmt(p.value)}</p>
      ))}
    </div>
  );
};

export const CashFlowTimeline = ({ cf30, cf60, cf90, loading }: Props) => {
  const [period, setPeriod] = useState<Period>(30);

  const cfMap: Record<Period, CashFlowPeriod> = { 30: cf30, 60: cf60, 90: cf90 };
  const current = cfMap[period];

  if (loading) return <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />;

  // Build weekly breakdown
  const buildWeeklyChart = (items: CashFlowPeriod['items'], days: number) => {
    const weeks = Math.ceil(days / 7);
    const now = new Date();
    return Array.from({ length: weeks }, (_, i) => {
      const weekStart = new Date(now.getTime() + i * 7 * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(now.getTime() + (i + 1) * 7 * 24 * 60 * 60 * 1000);
      const ws = weekStart.toISOString().split('T')[0];
      const we = weekEnd.toISOString().split('T')[0];
      const weekItems = items.filter(it => it.date >= ws && it.date < we);
      const income = weekItems.filter(i => i.type === 'receita').reduce((s, i) => s + i.amount, 0);
      const expenses = weekItems.filter(i => i.type === 'despesa').reduce((s, i) => s + i.amount, 0);
      return { semana: `Sem ${i + 1}`, income, expenses };
    });
  };

  const chartData = buildWeeklyChart(current.items, period);

  const expenseItems = current.items.filter(i => i.type === 'despesa').sort((a, b) => a.date.localeCompare(b.date));
  const incomeItems = current.items.filter(i => i.type === 'receita').sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-[15px] font-700 text-gray-900">Fluxo de Caixa Futuro</h3>
          <p className="text-[12px] text-gray-400 mt-0.5">Entradas e saídas previstas com base em pendências e recorrências</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {([30, 60, 90] as Period[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-[11px] font-600 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                period === p ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {p} dias
            </button>
          ))}
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <p className="text-[10px] text-gray-500 font-500 mb-0.5">Entradas Previstas</p>
          <p className="text-[16px] font-700 text-emerald-600">{fmt(current.income)}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-[10px] text-gray-500 font-500 mb-0.5">Saídas Previstas</p>
          <p className="text-[16px] font-700 text-red-500">{fmt(current.expenses)}</p>
        </div>
        <div className={`${current.net >= 0 ? 'bg-gray-50 border-gray-200' : 'bg-orange-50 border-orange-200'} border rounded-xl px-4 py-3`}>
          <p className="text-[10px] text-gray-500 font-500 mb-0.5">Saldo Líquido</p>
          <p className={`text-[16px] font-700 ${current.net >= 0 ? 'text-gray-800' : 'text-orange-600'}`}>{fmt(current.net)}</p>
        </div>
      </div>

      {/* Chart */}
      {chartData.some(d => d.income > 0 || d.expenses > 0) ? (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis dataKey="semana" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `R$${(v/1000).toFixed(0)}k` : `R$${v}`} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="income" name="Entradas" fill="#10B981" radius={[4,4,0,0]} />
            <Bar dataKey="expenses" name="Saídas" fill="#F97316" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-[13px] gap-2">
          <i className="ri-calendar-check-line text-3xl text-gray-200" />
          Nenhum lançamento previsto nos próximos {period} dias
        </div>
      )}

      {/* Items list */}
      {current.items.length > 0 && (
        <div className="space-y-3">
          {expenseItems.length > 0 && (
            <div>
              <h4 className="text-[11px] font-600 text-gray-400 uppercase tracking-wider mb-2">Saídas previstas</h4>
              <div className="space-y-1.5">
                {expenseItems.slice(0, 8).map(item => (
                  <div key={item.id} className="flex items-center gap-3 py-1.5">
                    <span className="text-[11px] text-gray-400 w-16 shrink-0">{fmtDate(item.date)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] text-gray-800 font-500 truncate">{item.description}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-gray-400">{item.category}</span>
                        {item.recurrence && (
                          <span className="text-[10px] bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded-full font-500">
                            <i className="ri-repeat-line mr-0.5" />{RECURRENCE_PT[item.recurrence] ?? item.recurrence}
                          </span>
                        )}
                        {item.installments > 1 && (
                          <span className="text-[10px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-500">
                            {item.installments}x
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-[13px] font-700 text-red-500 whitespace-nowrap shrink-0">-{fmt(item.amount)}</span>
                  </div>
                ))}
                {expenseItems.length > 8 && (
                  <p className="text-[11px] text-gray-400 text-center py-1">+{expenseItems.length - 8} mais</p>
                )}
              </div>
            </div>
          )}

          {incomeItems.length > 0 && (
            <div>
              <h4 className="text-[11px] font-600 text-gray-400 uppercase tracking-wider mb-2">Entradas previstas</h4>
              <div className="space-y-1.5">
                {incomeItems.slice(0, 5).map(item => (
                  <div key={item.id} className="flex items-center gap-3 py-1.5">
                    <span className="text-[11px] text-gray-400 w-16 shrink-0">{fmtDate(item.date)}</span>
                    <p className="flex-1 text-[12px] text-gray-800 font-500 truncate">{item.description}</p>
                    <span className="text-[13px] font-700 text-emerald-600 whitespace-nowrap shrink-0">+{fmt(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
